<template>
  <div class="ws">
    <header class="ws-hero">
      <div class="ws-hero__bg" aria-hidden="true">
        <span class="ws-hero__glow"></span>
        <span class="ws-hero__grid"></span>
      </div>
      <div class="ws-hero__main">
        <AltLogo />
        <h1 class="ws-hero__title">Сводка логиста</h1>
        <p class="ws-hero__lead">
          Что требует внимания прямо сейчас. Время погрузки и выгрузки — местное время склада.
          <span v-if="updatedAt" class="ws-hero__updated">Обновлено в {{ updatedAt }}</span>
        </p>
        <div class="ws-hero__controls">
          <div class="ws-seg" role="group" aria-label="Чьи рейсы показывать">
            <button
              v-for="option in scopes"
              :key="option.value"
              type="button"
              :class="['ws-seg__btn', { 'is-active': scope === option.value }]"
              @click="setScope(option.value)"
            >{{ option.label }}</button>
          </div>
          <button class="ws-refresh" type="button" :disabled="pending" @click="load">
            <span :class="['ws-refresh__icon', { 'is-spin': pending }]" aria-hidden="true">↻</span>
            {{ pending ? 'Обновляем…' : 'Обновить' }}
          </button>
        </div>
      </div>
      <TripsGlobe class="ws-hero__globe" :scope="scope === 'mine' ? 'mine' : 'all'" />
    </header>

    <div v-if="summary" class="ws-kpis">
      <button
        v-for="(kpi, index) in kpis"
        :key="kpi.key"
        type="button"
        :class="['ws-kpi', `ws-kpi--${kpi.tone}`, { 'is-link': kpi.group }]"
        :style="{ '--i': index }"
        :title="kpi.group ? 'Показать рейсы' : ''"
        @click="jump(kpi)"
      >
        <span class="ws-kpi__glow" aria-hidden="true"></span>
        <span class="ws-kpi__value">{{ shownKpi(kpi) }}</span>
        <span class="ws-kpi__label">{{ kpi.label }}</span>
        <span v-if="kpi.group && kpi.value" class="ws-kpi__go" aria-hidden="true">→</span>
      </button>
    </div>

    <!-- Светофор (решение Анны 03.11.2026): зелёное техническое состояние свёрнуто в одно окно. -->
    <TrafficBoard v-if="systemBoard" :board="systemBoard" collapsible :loading="lightsPending" />
    <SystemStatus v-else :health="health" :error="healthError" :loading="healthPending" />
    <TrafficBoard v-if="logistBoard" :board="logistBoard" :loading="lightsPending" @open="openGroup" />

    <ClaimsStrip />

    <div v-if="summary && summary.critical_by_site && summary.critical_by_site.length" class="ws-sites">
      <span class="ws-sites__title">Критичные по площадкам</span>
      <span v-for="s in summary.critical_by_site" :key="s.site" class="ws-sites__chip">{{ s.site }} <b>{{ s.count }}</b></span>
    </div>

    <WhereIsTrip @report="openReport" />

    <div v-if="error" class="ws-error">
      <strong>Сводку загрузить не удалось.</strong> {{ error }}
      <button type="button" class="ws-error__btn" @click="load">Повторить</button>
      <button type="button" class="ws-error__btn" @click="openReport('')">Сообщить в IT</button>
    </div>

    <div v-else-if="!summary && pending" class="ws-skeleton">
      <span v-for="n in 5" :key="n"></span>
    </div>

    <section v-if="summary" class="ws-groups">
      <article
        v-for="(group, index) in summary.groups"
        :id="`group-${group.key}`"
        :key="group.key"
        :class="['wg', `wg--${group.level}`, { 'is-empty': !group.count, 'is-open': isOpen(group.key) }]"
        :style="{ '--i': index }"
      >
        <button class="wg__head" type="button" :disabled="!group.count" @click="toggle(group.key)">
          <span class="wg__badge"><b>{{ group.count }}</b></span>
          <span class="wg__titles">
            <span class="wg__title">{{ group.title }}</span>
            <span class="wg__hint">{{ group.hint }}</span>
          </span>
          <span class="wg__level">{{ levelLabel(group.level) }}</span>
          <span v-if="group.count" class="wg__chevron" aria-hidden="true">›</span>
        </button>

        <transition name="wg-expand">
          <div v-if="isOpen(group.key) && group.count" class="wg__body">
            <div v-if="group.sites && group.sites.length > 1" class="wg__sites">
              <button
                type="button"
                :class="['ws-chip', { 'is-active': !siteFilter[group.key] }]"
                @click="setSite(group.key, '')"
              >Все площадки</button>
              <button
                v-for="s in group.sites"
                :key="s.site"
                type="button"
                :class="['ws-chip', { 'is-active': siteFilter[group.key] === s.site }]"
                @click="setSite(group.key, s.site)"
              >{{ s.site }} · {{ s.count }}</button>
            </div>
            <!-- Телефон: рейсы карточками (решение Анны 04.11.2026) -->
            <ul class="ws-cards">
              <li v-for="trip in filterBySite(group.trips, siteFilter[group.key])" :key="'c' + trip.id" :class="urgencyClass(trip)">
                <router-link :to="'/trips/' + trip.id" class="wc">
                  <span class="wc__top">
                    <b class="wc__num">{{ trip.trip_id || trip.id }}</b>
                    <span v-if="trip.is_top" class="trips__top" title="Топ-клиент">ТОП</span>
                    <span class="wc__status">{{ trip.status || '—' }}</span>
                  </span>
                  <span class="wc__route">{{ route(trip) }}</span>
                  <span v-if="trip.client" class="wc__client">{{ trip.client }}</span>
                  <span class="wc__times">
                    <span><em>Погрузка</em>{{ formatLocal(trip.load_at) }}<small>{{ relativeToNow(trip.load_in_minutes) }}</small></span>
                    <span><em>Выгрузка</em>{{ formatLocal(trip.unload_at) }}<small>{{ relativeToNow(trip.unload_in_minutes) }}</small></span>
                  </span>
                  <span
                    v-if="trip.deadline_at && needsData(trip)"
                    :class="['trips__deadline', { 'is-missed': trip.deadline_in_minutes < 0 }]"
                  >{{ deadlineText(trip) }}</span>
                  <span v-if="trip.problem" class="trips__problem">{{ trip.problem }}</span>
                  <span v-if="trip.head_reason" class="trips__problem">{{ trip.head_reason }}</span>
                  <span v-if="trip.rate_unconfirmed" class="trips__problem">Ставка перевозчику не подтверждена</span>
                  <span class="wc__foot">{{ trip.responsible || 'Без ответственного' }}</span>
                </router-link>
              </li>
            </ul>
            <div class="wg__table">
              <table class="trips">
                <thead>
                  <tr>
                    <th>Рейс</th>
                    <th>Площадка / маршрут</th>
                    <th>Клиент</th>
                    <th>Погрузка</th>
                    <th>Выгрузка</th>
                    <th>Статус</th>
                    <th>Ответственный</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="trip in filterBySite(group.trips, siteFilter[group.key])" :key="trip.id" :class="urgencyClass(trip)">
                    <td>
                      <router-link :to="`/trips/${trip.id}`" class="trips__link">{{ trip.trip_id || trip.id }}</router-link>
                      <span v-if="trip.is_top" class="trips__top" title="Топ-клиент">ТОП</span>
                    </td>
                    <td>
                      <div v-if="trip.site" class="trips__site">{{ trip.site }}</div>
                      <div class="trips__route">{{ route(trip) }}</div>
                    </td>
                    <td>{{ trip.client || '—' }}</td>
                    <td>
                      <div class="trips__time">{{ formatLocal(trip.load_at) }}</div>
                      <div class="trips__rel">{{ relativeToNow(trip.load_in_minutes) }}</div>
                      <div
                        v-if="trip.deadline_at && needsData(trip)"
                        :class="['trips__deadline', { 'is-missed': trip.deadline_in_minutes < 0 }]"
                      >{{ deadlineText(trip) }}</div>
                    </td>
                    <td>
                      <div class="trips__time">{{ formatLocal(trip.unload_at) }}</div>
                      <div class="trips__rel">{{ relativeToNow(trip.unload_in_minutes) }}</div>
                    </td>
                    <td>
                      <span class="trips__status">{{ trip.status || '—' }}</span>
                      <div v-if="trip.problem" class="trips__problem">{{ trip.problem }}</div>
                    </td>
                    <td>
                      <div>{{ trip.responsible || '—' }}</div>
                      <div v-if="trip.hypothesis" class="trips__hyp" title="Гипотеза по журналу назначений ERP">
                        {{ hypothesisText(trip.hypothesis) }}
                      </div>
                      <div v-if="trip.rate_unconfirmed" class="trips__problem">Ставка перевозчику не подтверждена</div>
                      <div v-if="trip.head_reason" class="trips__problem">{{ trip.head_reason }}</div>
                      <div v-if="trip.from_tms" class="trips__tms">Исполнитель из TMS, не через ERP</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="group.truncated" class="wg__more">
              Показаны первые {{ group.trips.length }} из {{ group.count }} — самые срочные.
            </p>
          </div>
        </transition>
      </article>
    </section>

    <!-- «Показатели группы» переехали в раздел «Финансы» (решение Анны 02.11.2026) — только руководителю логистики. -->
    <router-link v-if="SHOW_TEAM_GOALS" to="/finance" class="ws-finance">
      <span class="ws-finance__coin" aria-hidden="true">₽</span>
      <span class="ws-finance__text"><b>Финансы группы</b> — план месяца, выполнение на сегодня и гипотеза по месяцу</span>
      <span class="ws-finance__go">Открыть →</span>
    </router-link>

    <ReportToIT :open="reportOpen" :trip="reportTrip" :health="health" @close="reportOpen = false" />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import AltLogo from '@/components/workspace/AltLogo.vue'
import TripsGlobe from '@/components/workspace/TripsGlobe.vue'
import SystemStatus from '@/components/workspace/SystemStatus.vue'
import TrafficBoard from '@/components/workspace/TrafficBoard.vue'
import ClaimsStrip from '@/components/workspace/ClaimsStrip.vue'
import WhereIsTrip from '@/components/workspace/WhereIsTrip.vue'
import ReportToIT from '@/components/workspace/ReportToIT.vue'
import { fetchTeamGoalsAccess, fetchWorkspaceHealth, fetchWorkspaceLights, fetchWorkspaceSummary } from '@/backend/workspaceApi'
import {
  LEVEL_LABELS,
  deadlineText,
  filterBySite,
  formatLocal,
  hypothesisText,
  initiallyOpen,
  relativeToNow,
  route,
  urgencyClass,
} from '@/pages/workspace/workspaceLogic.mjs'

const REFRESH_MS = 2 * 60 * 1000
const COUNT_MS = 900
// «Показатели группы» видит только руководитель логистики (решение Анны 01.11.2026).
// Решает сервер: остальным блок не показывается, а данные им не отдаются.
const SHOW_TEAM_GOALS = ref(false)
const SCOPE_KEY = 'logistSummaryScope'

function readScope() {
  try {
    const saved = window.localStorage.getItem(SCOPE_KEY)
    return saved === 'mine' || saved === 'control' ? saved : 'all'
  } catch (e) {
    return 'all'
  }
}

const store = useStore()
// «Руководителю на контроль» — рейсы, где исполнитель пришёл из TMS мимо ERP и менеджера не определить,
// «левые» данные в погруженном рейсе, остановленная синхронизация, неподтверждённые ставки.
const isSupervisor = computed(() => Boolean(store.getters.isSupervisor))
const scopes = computed(() => [
  { value: 'all', label: 'Все рейсы' },
  { value: 'mine', label: 'Мои рейсы' },
  ...(isSupervisor.value ? [{ value: 'control', label: 'Руководителю на контроль' }] : []),
])
const scope = ref(readScope())
const summary = ref(null)
const pending = ref(false)
const error = ref('')
const updatedAt = ref('')
const health = ref(null)
const healthPending = ref(false)
const healthError = ref('')
const lights = ref(null)
const lightsPending = ref(false)
const systemBoard = computed(() => (lights.value?.boards || []).find((b) => b.key === 'system') || null)
const logistBoard = computed(() => (lights.value?.boards || []).find((b) => b.key === 'logist') || null)
const opened = ref(new Set())
const touched = ref(false)
const reportOpen = ref(false)
const siteFilter = ref({})
const shown = reactive({})
// Статусы без реального исполнителя: 1 Новый, 9 Открыт повторно, 10 Случайный исполнитель.
const NEEDS_DATA_STATUSES = new Set([1, 9, 10])
const reportTrip = ref('')
let timer = null
let frame = null
const reduceMotion = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const kpis = computed(() => {
  const t = summary.value?.totals || {}
  const firstCritical = (summary.value?.groups || []).find((g) => g.level === 'critical' && g.count)
  return [
    { key: 'critical', label: 'Горит', value: t.critical ?? 0, tone: t.critical ? 'critical' : 'calm', group: firstCritical?.key },
    { key: 'deadline', label: 'Критичные: срок данных прошёл', value: t.critical_no_executor ?? 0, tone: t.critical_no_executor ? 'critical' : 'calm', group: 'deadline_missed' },
    { key: 'needs', label: 'Без реального исполнителя', value: t.needs_executor ?? 0, tone: t.needs_executor ? 'warning' : 'calm', group: 'deadline_soon' },
    { key: 'lost', label: 'Пропали из TMS', value: t.lost_in_tms ?? 0, tone: t.lost_in_tms ? 'warning' : 'calm', group: 'lost_in_tms' },
    { key: 'transit', label: 'В пути', value: t.in_transit ?? 0, tone: 'info', group: 'in_transit' },
    { key: 'active', label: 'Активных рейсов', value: t.active ?? 0, tone: 'info', group: null },
  ]
})

function shownKpi(kpi) {
  return kpi.key in shown ? Math.round(shown[kpi.key]) : kpi.value
}

function animateKpis() {
  if (frame) window.cancelAnimationFrame(frame)
  const targets = kpis.value.map((k) => ({ key: k.key, from: shown[k.key] ?? 0, to: Number(k.value) || 0 }))
  if (reduceMotion) {
    targets.forEach((k) => { shown[k.key] = k.to })
    return
  }
  const start = performance.now()
  const step = (now) => {
    const p = Math.min(1, (now - start) / COUNT_MS)
    const eased = 1 - (1 - p) ** 3
    targets.forEach((k) => { shown[k.key] = k.from + (k.to - k.from) * eased })
    frame = p < 1 ? window.requestAnimationFrame(step) : null
  }
  frame = window.requestAnimationFrame(step)
}

async function jump(kpi) {
  if (!kpi.group || !kpi.value) return
  touched.value = true
  const next = new Set(opened.value)
  next.add(kpi.group)
  opened.value = next
  await nextTick()
  document.getElementById(`group-${kpi.group}`)?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
}

function needsData(trip) {
  return NEEDS_DATA_STATUSES.has(trip.status_id)
}

function setSite(groupKey, site) {
  siteFilter.value = { ...siteFilter.value, [groupKey]: site }
}

function levelLabel(level) {
  return LEVEL_LABELS[level] || ''
}

function isOpen(key) {
  return opened.value.has(key)
}

function toggle(key) {
  touched.value = true
  const next = new Set(opened.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  opened.value = next
}

async function loadSummary() {
  pending.value = true
  try {
    const data = await fetchWorkspaceSummary(scope.value)
    summary.value = data
    error.value = ''
    updatedAt.value = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    if (!touched.value) opened.value = initiallyOpen(data.groups)
    animateKpis()
  } catch (e) {
    error.value = e?.message || 'Неизвестная ошибка'
  } finally {
    pending.value = false
  }
}

async function loadHealth() {
  healthPending.value = true
  try {
    health.value = await fetchWorkspaceHealth()
    healthError.value = ''
  } catch (e) {
    healthError.value = e?.message || 'нет связи'
  } finally {
    healthPending.value = false
  }
}

// Светофор: если сервер ещё без него или ответил ошибкой — показываем прежнее «Состояние системы».
async function loadLights() {
  lightsPending.value = true
  try {
    lights.value = await fetchWorkspaceLights(scope.value === 'mine' ? 'mine' : 'all')
  } catch (e) {
    lights.value = null
  } finally {
    lightsPending.value = false
  }
}

function openGroup(key) {
  return jump({ group: key, value: 1 })
}

function load() {
  return Promise.all([loadSummary(), loadHealth(), loadLights()])
}

function setScope(value) {
  if (scope.value === value) return
  scope.value = value
  try {
    window.localStorage.setItem(SCOPE_KEY, value)
  } catch (e) {
    /* localStorage может быть недоступен */
  }
  touched.value = false
  loadSummary()
}

function openReport(number) {
  reportTrip.value = number || ''
  reportOpen.value = true
}

onMounted(() => {
  load()
  fetchTeamGoalsAccess().then((allowed) => { SHOW_TEAM_GOALS.value = allowed }).catch(() => { SHOW_TEAM_GOALS.value = false })
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') load()
  }, REFRESH_MS)
})

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer)
  if (frame) window.cancelAnimationFrame(frame)
})
</script>

<style scoped>
.ws { --gold: #f2b705; --gold-2: #ffe08a; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df;
  --critical: #dc2626; --warning: #d97706; --info: #6b7a99; --calm: #16a34a;
  max-width: 1280px; margin: 0 auto; padding: 24px 20px 80px; display: flex; flex-direction: column; gap: 16px; color: var(--ink); }

/* ---------- hero ---------- */
.ws-hero { position: relative; overflow: hidden; isolation: isolate; display: grid; grid-template-columns: minmax(0, 1fr) minmax(300px, 420px);
  gap: 18px; align-items: center; padding: 26px 28px; border-radius: 24px; color: #fff;
  background: radial-gradient(120% 140% at 0% 0%, #2b3142 0%, #1f2430 45%, #15171d 100%);
  box-shadow: 0 30px 60px -30px rgba(31, 36, 48, .55); }
.ws-hero__bg { position: absolute; inset: 0; z-index: -1; pointer-events: none; }
.ws-hero__glow { position: absolute; right: 4%; top: -45%; width: 560px; height: 560px; border-radius: 50%;
  background: radial-gradient(circle, rgba(242, 183, 5, .30), rgba(242, 183, 5, 0) 62%); animation: ws-breathe 9s ease-in-out infinite; }
.ws-hero__grid { position: absolute; inset: 40% -10% -20% -10%; opacity: .12;
  background-image: linear-gradient(rgba(255, 224, 138, .35) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 224, 138, .35) 1px, transparent 1px);
  background-size: 46px 46px; transform: perspective(600px) rotateX(60deg); transform-origin: bottom;
  mask-image: linear-gradient(to top, #000, transparent 80%); animation: ws-grid 16s linear infinite; }
.ws-hero__main { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.ws-hero__title { margin: 10px 0 0; font-size: 38px; letter-spacing: -.02em; line-height: 1.1;
  background: linear-gradient(90deg, #fff 0%, #fff 40%, var(--gold-2) 50%, #fff 60%, #fff 100%); background-size: 250% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent; animation: ws-shine 7s ease-in-out infinite; }
.ws-hero__lead { margin: 0; color: #c9ccd4; line-height: 1.55; max-width: 560px; }
.ws-hero__updated { display: inline-block; margin-left: 6px; padding: 1px 8px; border-radius: 999px; font-size: 12px;
  color: var(--gold-2); background: rgba(242, 183, 5, .12); border: 1px solid rgba(242, 183, 5, .25); }
.ws-hero__controls { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 6px; }
.ws-hero__globe { min-width: 0; }
.ws-seg { position: relative; display: inline-flex; flex-wrap: wrap; padding: 4px; border-radius: 999px; background: rgba(255, 255, 255, .07);
  border: 1px solid rgba(255, 255, 255, .12); backdrop-filter: blur(8px); }
.ws-seg__btn { border: 0; background: transparent; color: #d6d8de; padding: 8px 14px; border-radius: 999px; font: inherit;
  font-size: 13.5px; white-space: nowrap; cursor: pointer; transition: background .25s, color .25s, transform .2s; }
.ws-seg__btn:hover { color: #fff; }
.ws-seg__btn.is-active { background: linear-gradient(135deg, var(--gold-2), var(--gold)); color: var(--ink); font-weight: 650;
  box-shadow: 0 6px 16px -8px rgba(242, 183, 5, .9); }
.ws-refresh { display: inline-flex; align-items: center; gap: 8px; border: 1px solid rgba(255, 255, 255, .16); color: #fff;
  background: rgba(255, 255, 255, .06); border-radius: 999px; padding: 8px 16px; font: inherit; font-size: 13.5px; cursor: pointer;
  transition: border-color .2s, transform .2s; }
.ws-refresh:hover:not(:disabled) { border-color: var(--gold); transform: translateY(-1px); }
.ws-refresh:disabled { opacity: .7; cursor: default; }
.ws-refresh__icon { display: inline-block; font-size: 15px; color: var(--gold-2); }
.ws-refresh__icon.is-spin { animation: ws-spin 1s linear infinite; }

/* ---------- KPI-плашки ---------- */
.ws-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; perspective: 1000px; }
.ws-kpi { --c: var(--info); position: relative; overflow: hidden; display: flex; flex-direction: column; align-items: flex-start;
  gap: 4px; padding: 16px 16px 14px; text-align: left; font: inherit; color: var(--ink); cursor: default; border-radius: 18px;
  background: #fff; border: 1px solid var(--line); box-shadow: 0 1px 0 rgba(31, 36, 48, .03);
  transition: transform .25s cubic-bezier(.2, .8, .2, 1), box-shadow .25s, border-color .25s;
  animation: ws-rise .7s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(var(--i, 0) * 60ms); }
.ws-kpi::before { content: ''; position: absolute; left: 0; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0; background: var(--c); }
.ws-kpi.is-link { cursor: pointer; }
.ws-kpi.is-link:hover { transform: translateY(-4px) rotateX(4deg); border-color: color-mix(in srgb, var(--c) 45%, var(--line));
  box-shadow: 0 18px 34px -20px color-mix(in srgb, var(--c) 70%, transparent); }
.ws-kpi__glow { position: absolute; right: -40px; top: -40px; width: 120px; height: 120px; border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle, color-mix(in srgb, var(--c) 22%, transparent), transparent 70%); }
.ws-kpi__value { font-size: 32px; font-weight: 800; line-height: 1.05; color: var(--c); font-variant-numeric: tabular-nums; }
.ws-kpi__label { font-size: 12.5px; color: var(--muted); line-height: 1.3; padding-right: 14px; }
.ws-kpi__go { position: absolute; right: 14px; bottom: 12px; color: var(--c); font-weight: 700; opacity: 0;
  transform: translateX(-6px); transition: opacity .2s, transform .2s; }
.ws-kpi.is-link:hover .ws-kpi__go { opacity: 1; transform: none; }
.ws-kpi--critical { --c: var(--critical); background: linear-gradient(180deg, #fff 0%, #fff6f5 100%); }
.ws-kpi--critical .ws-kpi__value { animation: ws-heat 2.4s ease-in-out infinite; }
.ws-kpi--warning { --c: var(--warning); background: linear-gradient(180deg, #fff 0%, #fffaf0 100%); }
.ws-kpi--calm { --c: var(--calm); }
.ws-kpi--info { --c: #8a6d00; }

/* ---------- площадки ---------- */
.ws-sites { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 13px; }
.ws-sites__title { color: var(--muted); font-weight: 600; margin-right: 2px; }
.ws-sites__chip { display: inline-flex; gap: 6px; align-items: center; background: #fff5f5; border: 1px solid #fecaca; color: #7f1d1d;
  border-radius: 999px; padding: 4px 12px; animation: ws-rise .6s backwards; }
.ws-sites__chip b { color: var(--critical); }

.ws-error { border: 1px solid #fecaca; background: #fef2f2; color: #7f1d1d; border-radius: 14px; padding: 12px 16px; display: flex;
  gap: 10px; align-items: center; flex-wrap: wrap; font-size: 14px; }
.ws-error__btn { border: 1px solid #fca5a5; background: #fff; color: #b91c1c; border-radius: 10px; padding: 6px 12px; font: inherit; cursor: pointer; }
.ws-skeleton { display: flex; flex-direction: column; gap: 10px; }
.ws-skeleton span { height: 64px; border-radius: 16px; background: linear-gradient(90deg, #f1f0ec 25%, #faf9f6 50%, #f1f0ec 75%);
  background-size: 200% 100%; animation: ws-skeleton 1.2s linear infinite; }

/* ---------- группы-плашки ---------- */
.ws-groups { display: flex; flex-direction: column; gap: 10px; }
.wg { --c: var(--info); position: relative; border-radius: 18px; background: #fff; border: 1px solid var(--line); overflow: hidden;
  scroll-margin-top: 16px; transition: box-shadow .25s, border-color .25s, transform .25s;
  animation: ws-rise .6s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(var(--i, 0) * 40ms); }
.wg::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 5px; background: var(--c); }
.wg--critical { --c: var(--critical); }
.wg--warning { --c: var(--warning); }
.wg--info { --c: #b38b1d; }
.wg:not(.is-empty):hover { box-shadow: 0 16px 34px -24px rgba(31, 36, 48, .5); transform: translateY(-1px); }
.wg.is-open { box-shadow: 0 20px 40px -26px rgba(31, 36, 48, .55); border-color: color-mix(in srgb, var(--c) 30%, var(--line)); }
.wg.is-empty { opacity: .6; }
.wg.is-empty::before { background: #e5e7eb; }
.wg__head { display: flex; align-items: center; gap: 16px; width: 100%; padding: 14px 18px 14px 20px; border: 0; background: none;
  font: inherit; text-align: left; cursor: pointer; color: inherit; }
.wg__head:disabled { cursor: default; }
.wg__badge { flex: none; display: grid; place-items: center; min-width: 52px; height: 52px; padding: 0 10px; border-radius: 16px;
  background: color-mix(in srgb, var(--c) 10%, #fff); border: 1px solid color-mix(in srgb, var(--c) 25%, transparent); }
.wg__badge b { font-size: 22px; font-weight: 800; color: var(--c); font-variant-numeric: tabular-nums; }
.wg--critical:not(.is-empty) .wg__badge { animation: ws-pulse 2.2s ease-out infinite; }
.wg.is-empty .wg__badge { background: #f6f6f4; border-color: #ecebe7; }
.wg.is-empty .wg__badge b { color: #9ca3af; }
.wg__titles { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.wg__title { font-weight: 650; font-size: 15px; }
.wg__hint { font-size: 12.5px; color: var(--muted); line-height: 1.4; }
.wg__level { flex: none; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--c);
  padding: 4px 10px; border-radius: 999px; background: color-mix(in srgb, var(--c) 9%, transparent); }
.wg__chevron { flex: none; display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; font-size: 20px;
  color: var(--muted); background: #f6f6f4; transition: transform .3s cubic-bezier(.2, .8, .2, 1), background .2s, color .2s; }
.wg.is-open .wg__chevron { transform: rotate(90deg); background: var(--ink); color: #fff; }
.wg__body { border-top: 1px solid #f1f0ec; }
.wg__sites { display: flex; flex-wrap: wrap; gap: 6px; padding: 12px 18px 0 20px; }
.wg__table { overflow-x: auto; padding: 6px 8px 8px 12px; }
.wg__more { margin: 0; padding: 4px 20px 14px; font-size: 12.5px; color: var(--muted); }
.wg-expand-enter-active, .wg-expand-leave-active { transition: opacity .3s, transform .3s cubic-bezier(.2, .8, .2, 1); }
.wg-expand-enter-from, .wg-expand-leave-to { opacity: 0; transform: translateY(-8px); }

.ws-chip { border: 1px solid var(--line); background: #fff; border-radius: 999px; padding: 5px 12px; font: inherit; font-size: 12.5px;
  cursor: pointer; color: #374151; transition: all .2s; }
.ws-chip:hover { border-color: var(--gold); }
.ws-chip.is-active { background: var(--ink); border-color: var(--ink); color: #fff; }

.trips { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
.trips th { position: sticky; top: 0; z-index: 1; text-align: left; font-weight: 600; font-size: 11.5px; text-transform: uppercase;
  letter-spacing: .04em; color: #8b8f99; padding: 10px 12px; background: #fbfaf7; white-space: nowrap; }
.trips th:first-child { border-radius: 10px 0 0 10px; }
.trips th:last-child { border-radius: 0 10px 10px 0; }
.trips td { padding: 10px 12px; border-bottom: 1px solid #f3f2ee; vertical-align: top; }
.trips tbody tr { transition: background .15s; }
.trips tbody tr:hover td { background: #fffbeb; }
.trips tr.is-overdue td:first-child { box-shadow: inset 3px 0 0 var(--critical); }
.trips tr.is-soon td:first-child { box-shadow: inset 3px 0 0 #f59e0b; }
.trips__link { color: var(--ink); font-weight: 700; text-decoration: none; border-bottom: 2px solid rgba(242, 183, 5, .55);
  transition: border-color .2s, color .2s; }
.trips__link:hover { color: #8a6d00; border-color: var(--gold); }
.trips__top { margin-left: 6px; font-size: 10.5px; font-weight: 800; color: #6b4e00; background: linear-gradient(135deg, #ffe08a, #f2b705);
  border-radius: 6px; padding: 1px 6px; }
.trips__route { color: #374151; }
.trips__time { font-variant-numeric: tabular-nums; }
.trips__rel { font-size: 12px; color: var(--muted); }
.trips__site { font-size: 12px; font-weight: 650; color: #374151; }
.trips__status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; background: #f3f2ee; color: #374151; }
.trips__deadline { font-size: 12px; color: #92400e; margin-top: 2px; }
.trips__deadline.is-missed { color: #b91c1c; font-weight: 650; }
.trips__problem { font-size: 12px; color: #b91c1c; margin-top: 3px; }
.trips__hyp { font-size: 12px; color: #92400e; margin-top: 3px; }
.trips__tms { font-size: 11.5px; color: var(--muted); margin-top: 3px; }

.ws-finance { display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-radius: 16px; color: #fff;
  text-decoration: none; background: linear-gradient(135deg, #1f2430 0%, #2b3142 60%, #3b3420 100%);
  box-shadow: 0 16px 34px -20px rgba(31, 36, 48, .6); transition: transform .2s, box-shadow .2s; }
.ws-finance:hover { transform: translateY(-2px); box-shadow: 0 22px 40px -20px rgba(31, 36, 48, .7); }
.ws-finance__coin { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; font-weight: 800;
  color: #6b4e00; background: radial-gradient(circle at 32% 30%, #fff4c4, #f2b705 55%, #b8860b); flex: none; }
.ws-finance__text { flex: 1; color: #d6d8de; }
.ws-finance__text b { color: #fff; }
.ws-finance__go { color: #ffe08a; font-weight: 700; white-space: nowrap; }

@keyframes ws-rise { from { opacity: 0; transform: translateY(16px); } }
@keyframes ws-breathe { 0%, 100% { transform: scale(1); opacity: .85; } 50% { transform: scale(1.12); opacity: 1; } }
@keyframes ws-grid { from { background-position: 0 0; } to { background-position: 0 46px; } }
@keyframes ws-shine { 0%, 100% { background-position: 100% 0; } 50% { background-position: 0 0; } }
@keyframes ws-spin { to { transform: rotate(360deg); } }
@keyframes ws-heat { 0%, 100% { text-shadow: 0 0 0 rgba(220, 38, 38, 0); } 50% { text-shadow: 0 0 14px rgba(220, 38, 38, .35); } }
@keyframes ws-pulse { 0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, .35); } 100% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); } }
@keyframes ws-skeleton { from { background-position: 200% 0; } to { background-position: -200% 0; } }

@media (max-width: 1100px) {
  .ws-hero { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .ws { padding: 16px 12px 70px; }
  .ws-hero { padding: 20px 16px; }
  .ws-hero__title { font-size: 28px; }
  .wg__hint { display: none; }
  .wg__level { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .ws *, .ws *::before, .ws *::after { animation: none !important; transition: none !important; }
}
</style>

<style scoped>
/* Карточки рейсов «Сводки логиста» для телефона; на компьютере — таблица. */
.ws-cards { display: none; }
@media screen and (max-width: 768px) {
  .wg__table { display: none; }
  .ws-cards { display: grid; gap: 10px; list-style: none; margin: 8px 0 0; padding: 0; }
  .wc { display: flex; flex-direction: column; gap: 6px; padding: 13px 14px; border-radius: 16px; background: #fff;
    border: 1px solid #ebe7dd; border-left: 4px solid #d9d4c7; color: #1f2430; text-decoration: none;
    box-shadow: 0 10px 24px -20px rgba(31, 36, 48, .5); }
  .ws-cards li[class*='crit'] .wc, .ws-cards li[class*='overdue'] .wc, .ws-cards li[class*='red'] .wc { border-left-color: #dc2626; }
  .ws-cards li[class*='warn'] .wc, .ws-cards li[class*='soon'] .wc, .ws-cards li[class*='yellow'] .wc { border-left-color: #d97706; }
  .wc__top { display: flex; align-items: center; gap: 8px; }
  .wc__num { font-size: 16px; font-weight: 800; }
  .wc__status { margin-left: auto; font-size: 11.5px; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: #f3f1ec; }
  .wc__route { font-size: 15px; font-weight: 600; }
  .wc__client { font-size: 12.5px; color: #6b7180; }
  .wc__times { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12.5px; }
  .wc__times em { display: block; font-style: normal; font-size: 11px; color: #8a8f9c; }
  .wc__times small { display: block; font-size: 11px; color: #8a8f9c; }
  .wc__foot { font-size: 12.5px; color: #4b5161; padding-top: 6px; border-top: 1px dashed #ebe7dd; }
}
</style>
